import { faPalette } from '@fortawesome/pro-solid-svg-icons';
import { useFeatureGate } from '@statsig/react-bindings';
import { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';

import { RuntimeEnvironmentOptions, getEnvFromHostname } from '@growtherapy/analytics';
import { IconButton, useRebrand } from '@growtherapy/sprout-ui';

import { PROVIDER_PATH } from '../config';

const rebrandKey = 'rebrand-toggle';

// This is the only place in code that should change these classes
function toggleRebrand(enabled: boolean, rebrandClass: string) {
  window.document.documentElement.classList.toggle('rebrand', enabled);
  window.document.documentElement.classList.toggle(rebrandClass, enabled);

  localStorage.setItem(rebrandKey, enabled.toString());
}

export function RebrandToggle() {
  const rebrand = useRebrand();
  const isProduction = getEnvFromHostname({ hostname: location.hostname }) === RuntimeEnvironmentOptions.production;

  const { value } = useFeatureGate('rebrand_theme');
  // Since statsig does not provide a loading state, track it manually
  const [loadingFeatureGate, setLoadingFeatureGate] = useState(true);

  const isProviderView = !!useMatch(PROVIDER_PATH);
  const rebrandClass = isProviderView ? 'theme-provider' : 'theme-client';

  // turn rebrand on/off based on FF value, always turn on in dev
  useEffect(() => {
    // rebrand only applies to clients for now (provider for testing only)
    if (isProviderView && isProduction) return;

    if (isProduction) {
      // Use default value while loading, then use the actual gate value
      const defaultValue = isProviderView ? false : true;
      toggleRebrand(loadingFeatureGate ? defaultValue : value, rebrandClass);
      setLoadingFeatureGate(false);
    } else {
      toggleRebrand(true, rebrandClass);
    }
  }, [value, loadingFeatureGate, isProduction, isProviderView, rebrandClass]);

  // hide toggle for production and cypress tests
  // @ts-ignore window.testConfig is defined in cypress tests
  if (isProduction || window.testConfig) return null;

  return (
    <IconButton
      use='primary'
      iconDefinition={faPalette}
      aria-label="rebrand toggle"
      className="fixed bottom-32 right-3 z-50 opacity-50 transition-colors hover:opacity-100"
      onClick={() => toggleRebrand(!rebrand, rebrandClass)}
    />
  );
}
